import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useAuth } from '../contexts/AuthContext';
import {
  sendApprovalRequest as pushApprovalRequestAlert,
  sendApprovalResponse as pushApprovalResponseAlert
} from '../utils/messagingIntegration';

type ApprovalType = 'void' | 'discount' | 'refund' | 'comp' | 'price_override' | 'custom';
type ApprovalStatus = 'pending' | 'approved' | 'denied' | 'cancelled' | 'expired';

interface ApprovalRequest {
  approvalId: string;
  enterpriseId: string;
  storeId: string;
  type: ApprovalType;
  amount: number | null;
  reason: string;
  status: ApprovalStatus;
  requestingEmployeeId: string;
  requestingEmployeeName: string;
  requestedBy?: string;
  orderNumber?: string | null;
  tableNumber?: string | null;
  checkItemId?: string | null;
  menuItemId?: string | null;
  managerId?: string;
  managerName?: string;
  resolvedAt?: string;
  resolutionReason?: string;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
}

interface Store {
  storeId: string;
  name?: string;
  enterpriseId?: string;
}

interface ApprovalWorkflowProps {
  user: any;
  storeId?: string;
  enterpriseId?: string;
  /** When false, hide the "Submit test request" controls (useful for read-only dashboard embeds). */
  allowCreate?: boolean;
}

const APPROVAL_TYPES: { id: ApprovalType; label: string; color: string }[] = [
  { id: 'void', label: 'Void Item', color: '#dc2626' },
  { id: 'discount', label: 'Discount', color: '#d97706' },
  { id: 'refund', label: 'Refund', color: '#2563eb' },
  { id: 'comp', label: 'Comp', color: '#7c3aed' },
  { id: 'price_override', label: 'Price Override', color: '#0891b2' }
];

const POLL_MS = 15000;

const timeAgo = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime();
  if (!isFinite(ms)) return '';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const statusColor = (s: ApprovalStatus) => {
  switch (s) {
    case 'pending': return { bg: '#fef3c7', fg: '#92400e' };
    case 'approved': return { bg: '#dcfce7', fg: '#166534' };
    case 'denied': return { bg: '#fee2e2', fg: '#991b1b' };
    case 'cancelled': return { bg: '#f3f4f6', fg: '#374151' };
    case 'expired': return { bg: '#e5e7eb', fg: '#4b5563' };
    default: return { bg: '#f3f4f6', fg: '#111' };
  }
};

const ApprovalWorkflow: React.FC<ApprovalWorkflowProps> = ({
  user,
  storeId: initialStoreId,
  enterpriseId: initialEnterpriseId,
  allowCreate = true
}) => {
  const { getIdToken } = useAuth();

  const enterpriseId: string =
    initialEnterpriseId ||
    user?.enterpriseId ||
    user?.attributes?.['custom:enterpriseId'] ||
    '17527771077049luj0w1ek';

  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>(initialStoreId || '');
  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const [testForm, setTestForm] = useState<{
    type: ApprovalType;
    amount: string;
    reason: string;
    orderNumber: string;
    tableNumber: string;
  }>({
    type: 'void',
    amount: '',
    reason: '',
    orderNumber: '',
    tableNumber: ''
  });

  const authHeaders = useCallback(async (): Promise<HeadersInit> => {
    const token = await getIdToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  }, [getIdToken]);

  const loadStores = useCallback(async () => {
    try {
      const r = await fetch(API_ENDPOINTS.stores(), { headers: await authHeaders() });
      const body = await r.json().catch(() => null);
      const list: any[] = body?.data || body?.stores || (Array.isArray(body) ? body : []);
      const scoped = list
        .filter((s) => !enterpriseId || !s.enterpriseId || s.enterpriseId === enterpriseId)
        .map((s) => ({ storeId: s.storeId || s.id, name: s.name, enterpriseId: s.enterpriseId }));
      setStores(scoped);
      if (!selectedStoreId && scoped.length > 0) setSelectedStoreId(scoped[0].storeId);
    } catch {
      // leave the user to type a storeId manually if stores can't load
    }
  }, [authHeaders, enterpriseId, selectedStoreId]);

  const loadRequests = useCallback(async () => {
    if (!selectedStoreId) {
      setRequests([]);
      return;
    }
    try {
      const r = await fetch(API_ENDPOINTS.approvalsForStore(selectedStoreId, undefined, 100), { headers: await authHeaders() });
      const body = await r.json().catch(() => null);
      if (!r.ok || !body?.success) throw new Error(body?.message || `Load failed (${r.status})`);
      setRequests(Array.isArray(body.data) ? body.data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load approvals');
    }
  }, [authHeaders, selectedStoreId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      await loadStores();
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [loadStores]);

  useEffect(() => {
    if (!selectedStoreId) return;
    loadRequests();
    const id = window.setInterval(loadRequests, POLL_MS);
    return () => window.clearInterval(id);
  }, [selectedStoreId, loadRequests]);

  const pending = useMemo(() => requests.filter((r) => r.status === 'pending'), [requests]);
  const resolved = useMemo(() => requests.filter((r) => r.status !== 'pending').slice(0, 20), [requests]);

  const submitTestRequest = async () => {
    if (!selectedStoreId) {
      setError('Select a store first');
      return;
    }
    if (!testForm.reason.trim()) {
      setError('Reason is required');
      return;
    }
    setError(null);
    setMessage(null);
    try {
      const payload: any = {
        storeId: selectedStoreId,
        enterpriseId,
        type: testForm.type,
        reason: testForm.reason.trim(),
        amount: testForm.amount ? Number(testForm.amount) : null,
        orderNumber: testForm.orderNumber || null,
        tableNumber: testForm.tableNumber || null
      };
      const r = await fetch(API_ENDPOINTS.approvalsCreate(), {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(payload)
      });
      const body = await r.json().catch(() => null);
      if (!r.ok || !body?.success) throw new Error(body?.message || `Create failed (${r.status})`);
      const created: ApprovalRequest = body.data;
      setMessage(`Request ${created.approvalId.slice(-8)} submitted`);
      // Best-effort real-time alert via messaging-service; ignore failures.
      try {
        await pushApprovalRequestAlert({
          storeId: selectedStoreId,
          requestId: created.approvalId,
          approvalType: (created.type === 'void' || created.type === 'discount' || created.type === 'refund')
            ? created.type
            : 'discount',
          amount: created.amount ?? 0,
          reason: created.reason,
          requestingEmployeeId: created.requestingEmployeeId,
          requestingEmployeeName: created.requestingEmployeeName,
          tableNumber: created.tableNumber || undefined,
          orderNumber: created.orderNumber || undefined,
          senderId: created.requestingEmployeeId,
          senderName: created.requestingEmployeeName
        });
      } catch {}
      setTestForm({ type: testForm.type, amount: '', reason: '', orderNumber: '', tableNumber: '' });
      await loadRequests();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit request');
    }
  };

  const resolveRequest = async (req: ApprovalRequest, action: 'approve' | 'deny' | 'cancel', reason?: string) => {
    setProcessingId(req.approvalId);
    setError(null);
    setMessage(null);
    try {
      const r = await fetch(API_ENDPOINTS.approvalAction(req.approvalId, action), {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(reason ? { reason } : {})
      });
      const body = await r.json().catch(() => null);
      if (!r.ok || !body?.success) throw new Error(body?.message || `Action failed (${r.status})`);
      setMessage(`Request ${action}d`);
      if (action === 'approve' || action === 'deny') {
        try {
          await pushApprovalResponseAlert({
            storeId: req.storeId,
            requestId: req.approvalId,
            approved: action === 'approve',
            approvalType: (req.type === 'void' || req.type === 'discount' || req.type === 'refund')
              ? req.type
              : 'discount',
            amount: req.amount ?? 0,
            managerId: body.data?.managerId || user?.email || 'manager',
            managerName: body.data?.managerName || user?.email || 'Manager',
            requestingEmployeeId: req.requestingEmployeeId,
            reason
          });
        } catch {}
      }
      await loadRequests();
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to ${action}`);
    } finally {
      setProcessingId(null);
    }
  };

  const renderRequestCard = (req: ApprovalRequest, isPending: boolean) => {
    const sc = statusColor(req.status);
    const typeDef = APPROVAL_TYPES.find((t) => t.id === req.type);
    return (
      <div key={req.approvalId} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '0.75rem', marginBottom: '0.5rem', background: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ background: typeDef?.color || '#6b7280', color: 'white', padding: '0.125rem 0.5rem', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600 }}>
                {(typeDef?.label || req.type).toUpperCase()}
              </span>
              <span style={{ background: sc.bg, color: sc.fg, padding: '0.125rem 0.5rem', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600 }}>
                {req.status.toUpperCase()}
              </span>
              {req.amount != null && (
                <span style={{ fontWeight: 600 }}>${Number(req.amount).toFixed(2)}</span>
              )}
              <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>{timeAgo(req.createdAt)}</span>
            </div>
            <div style={{ marginTop: '0.25rem', fontSize: '0.95rem' }}>{req.reason}</div>
            <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#6b7280' }}>
              {req.requestingEmployeeName}
              {req.tableNumber ? ` · Table ${req.tableNumber}` : ''}
              {req.orderNumber ? ` · Order ${req.orderNumber}` : ''}
              {req.menuItemId ? ` · Item ${req.menuItemId}` : ''}
            </div>
            {!isPending && req.managerName && (
              <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#6b7280' }}>
                Resolved by {req.managerName} {req.resolvedAt ? `· ${timeAgo(req.resolvedAt)}` : ''}
                {req.resolutionReason ? ` · "${req.resolutionReason}"` : ''}
              </div>
            )}
          </div>
          {isPending && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: 110 }}>
              <button
                disabled={processingId === req.approvalId}
                onClick={() => {
                  const reason = window.prompt('Optional note for the server:') || undefined;
                  resolveRequest(req, 'approve', reason);
                }}
                style={{ padding: '0.4rem 0.6rem', background: '#16a34a', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
              >
                Approve
              </button>
              <button
                disabled={processingId === req.approvalId}
                onClick={() => {
                  const reason = window.prompt('Why is this denied?') || undefined;
                  resolveRequest(req, 'deny', reason);
                }}
                style={{ padding: '0.4rem 0.6rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
              >
                Deny
              </button>
              <button
                disabled={processingId === req.approvalId}
                onClick={() => resolveRequest(req, 'cancel')}
                style={{ padding: '0.4rem 0.6rem', background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem' }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading && stores.length === 0) {
    return <div style={{ padding: '1rem' }}>Loading approvals…</div>;
  }

  return (
    <div style={{ background: 'white', borderRadius: 8, padding: '1rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Approval Workflow</h3>
          <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>Voids, discounts, refunds, comps, price overrides</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label htmlFor="apv-store" style={{ fontSize: '0.85rem', color: '#374151' }}>Store:</label>
          {stores.length > 0 ? (
            <select
              id="apv-store"
              value={selectedStoreId}
              onChange={(e) => setSelectedStoreId(e.target.value)}
              style={{ padding: '0.35rem', border: '1px solid #d1d5db', borderRadius: 4 }}
            >
              <option value="">— select store —</option>
              {stores.map((s) => (
                <option key={s.storeId} value={s.storeId}>{s.name || s.storeId}</option>
              ))}
            </select>
          ) : (
            <input
              id="apv-store"
              type="text"
              value={selectedStoreId}
              placeholder="storeId"
              onChange={(e) => setSelectedStoreId(e.target.value)}
              style={{ padding: '0.35rem', border: '1px solid #d1d5db', borderRadius: 4, width: 220 }}
            />
          )}
          <button onClick={loadRequests} style={{ padding: '0.35rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 4, background: 'white', cursor: 'pointer' }}>Refresh</button>
        </div>
      </div>

      {error && <div role="alert" style={{ padding: '0.5rem 0.75rem', background: '#fee2e2', color: '#991b1b', borderRadius: 4, marginBottom: '0.5rem' }}>{error}</div>}
      {message && <div style={{ padding: '0.5rem 0.75rem', background: '#dcfce7', color: '#166534', borderRadius: 4, marginBottom: '0.5rem' }}>{message}</div>}

      {allowCreate && (
        <details style={{ marginBottom: '0.75rem' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Submit a request (servers + test)</summary>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.5rem', marginTop: '0.5rem' }}>
            <select value={testForm.type} onChange={(e) => setTestForm({ ...testForm, type: e.target.value as ApprovalType })} style={{ padding: '0.35rem' }}>
              {APPROVAL_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              <option value="custom">Custom</option>
            </select>
            <input type="number" step="0.01" placeholder="Amount" value={testForm.amount} onChange={(e) => setTestForm({ ...testForm, amount: e.target.value })} style={{ padding: '0.35rem' }} />
            <input type="text" placeholder="Table" value={testForm.tableNumber} onChange={(e) => setTestForm({ ...testForm, tableNumber: e.target.value })} style={{ padding: '0.35rem' }} />
            <input type="text" placeholder="Order #" value={testForm.orderNumber} onChange={(e) => setTestForm({ ...testForm, orderNumber: e.target.value })} style={{ padding: '0.35rem' }} />
            <input type="text" placeholder="Reason *" value={testForm.reason} onChange={(e) => setTestForm({ ...testForm, reason: e.target.value })} style={{ padding: '0.35rem', gridColumn: 'span 2' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <button onClick={submitTestRequest} style={{ padding: '0.4rem 0.8rem', background: '#0369a1', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
              Submit request
            </button>
          </div>
        </details>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div>
          <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem' }}>Pending ({pending.length})</h4>
          {pending.length === 0 ? (
            <div style={{ color: '#6b7280', padding: '1rem', border: '1px dashed #e5e7eb', borderRadius: 6, textAlign: 'center' }}>
              No pending requests.
            </div>
          ) : pending.map((r) => renderRequestCard(r, true))}
        </div>
        <div>
          <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem' }}>Recent decisions ({resolved.length})</h4>
          {resolved.length === 0 ? (
            <div style={{ color: '#6b7280', padding: '1rem', border: '1px dashed #e5e7eb', borderRadius: 6, textAlign: 'center' }}>
              No recent decisions.
            </div>
          ) : resolved.map((r) => renderRequestCard(r, false))}
        </div>
      </div>
    </div>
  );
};

export default ApprovalWorkflow;
